import { Injectable, signal, effect } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'auto';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_STORAGE_KEY = 'app-theme-mode';

  themeMode = signal<ThemeMode>(this.getStoredTheme());

  constructor() {
    effect(() => {
      const mode = this.themeMode();
      this.applyTheme(mode);
      localStorage.setItem(this.THEME_STORAGE_KEY, mode);
    });

    this.applyTheme(this.themeMode());
    this.listenToSystemThemeChanges();
  }

  setTheme(mode: ThemeMode): void {
    this.themeMode.set(mode);
  }

  private getStoredTheme(): ThemeMode {
    const stored = localStorage.getItem(this.THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored;
    }
    return 'auto';
  }

  private applyTheme(mode: ThemeMode): void {
    const html = document.documentElement;

    if (mode === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        html.classList.add('dark-mode');
      } else {
        html.classList.remove('dark-mode');
      }
      console.log('Theme applied (auto):', prefersDark ? 'dark' : 'light', 'Has dark-mode class:', html.classList.contains('dark-mode'));
    } else {
      if (mode === 'dark') {
        html.classList.add('dark-mode');
      } else {
        html.classList.remove('dark-mode');
      }
      console.log('Theme applied (manual):', mode, 'Has dark-mode class:', html.classList.contains('dark-mode'));
    }
  }

  private listenToSystemThemeChanges(): void {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (this.themeMode() === 'auto') {
        if (e.matches) {
          document.documentElement.classList.add('dark-mode');
        } else {
          document.documentElement.classList.remove('dark-mode');
        }
      }
    });
  }
}
